import type { ApiErrorBody, ErrorCode } from '@crave/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details: unknown;
  readonly requestId: string | undefined;
  constructor(
    status: number,
    code: ErrorCode,
    message: string,
    details?: unknown,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns the bearer token for the current principal, or null for anonymous calls. */
  getToken?: () => Promise<string | null> | string | null;
  /**
   * Called once when a request that carried a token gets 401. Return a fresh token to retry the
   * request with it, or null when the session cannot be recovered (the app then shows sign-in).
   */
  onUnauthorized?: () => Promise<string | null>;
  fetch?: typeof fetch;
}

export interface RequestOptions {
  /** Supplied for mutating calls that must be safe to retry; generated when omitted. */
  idempotencyKey?: string;
  etag?: string;
  signal?: AbortSignal;
}

export interface ApiResponse<T> {
  data: T;
  etag: string | null;
  status: number;
  /** True when the server answered 304 and `data` is the cached value passed in by the caller. */
  notModified: boolean;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function createApiClient(opts: ApiClientOptions) {
  const doFetch = opts.fetch ?? fetch;
  const base = opts.baseUrl.replace(/\/+$/, '');

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    ro: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const token = opts.getToken ? await opts.getToken() : null;
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (MUTATING.has(method)) headers['Idempotency-Key'] = ro.idempotencyKey ?? crypto.randomUUID();
    if (ro.etag) headers['If-None-Match'] = ro.etag;

    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    if (ro.signal) init.signal = ro.signal;
    let res = await doFetch(`${base}${path}`, init);
    if (res.status === 401 && token && opts.onUnauthorized) {
      const fresh = await opts.onUnauthorized().catch(() => null);
      if (fresh && fresh !== token) {
        headers['Authorization'] = `Bearer ${fresh}`;
        res = await doFetch(`${base}${path}`, { ...init, headers });
      }
    }
    const etag = res.headers.get('ETag');
    if (res.status === 304) return { data: undefined as T, etag, status: 304, notModified: true };
    const text = await res.text();
    let json: unknown = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        throw new ApiError(
          res.status >= 400 ? res.status : 502,
          'INTERNAL',
          `Unexpected response from the Crave server (HTTP ${res.status}).`,
        );
      }
    }
    if (!res.ok) {
      const err = (json as ApiErrorBody | null)?.error;
      throw new ApiError(
        res.status,
        err?.code ?? 'INTERNAL',
        err?.message ?? res.statusText,
        err?.details,
        err?.request_id,
      );
    }
    return { data: json as T, etag, status: res.status, notModified: false };
  }

  return {
    get: <T>(path: string, ro?: RequestOptions) => request<T>('GET', path, undefined, ro),
    post: <T>(path: string, body?: unknown, ro?: RequestOptions) =>
      request<T>('POST', path, body, ro),
    put: <T>(path: string, body?: unknown, ro?: RequestOptions) =>
      request<T>('PUT', path, body, ro),
    patch: <T>(path: string, body?: unknown, ro?: RequestOptions) =>
      request<T>('PATCH', path, body, ro),
    delete: <T>(path: string, ro?: RequestOptions) => request<T>('DELETE', path, undefined, ro),
  };
}
export type ApiClient = ReturnType<typeof createApiClient>;
